/**
 * SeriesDetailPage styles
 *
 * Task: T-P5-D5.2-F6-3
 *
 * Retained series-boundary detail styles with series-specific additions:
 * - Instance dates list
 * - Location row
 */

/* ======================
   Page Layout
   ====================== */

.page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  max-width: 480px;
  margin: 0 auto;
  background: var(--bg-page);
}

/* ======================
   Header
   ====================== */

.header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-4) var(--space-2);
  animation: fadeInUp 0.4s var(--ease-out) backwards;
}

.backBtn {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s var(--ease-out);
}

.backBtn:hover {
  background: var(--bg-card);
  color: var(--text-primary);
}

.backBtn:active {
  transform: scale(0.98);
}

.backBtn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* ======================
   Detail Section
   ====================== */

.detailSection {
  animation: fadeInUp 0.5s var(--ease-out) 0.1s backwards;
}

.imageContainer {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.imageFallback {
  width: 100%;
  height: 100%;
}

.detailContent {
  padding: var(--space-5) var(--space-4);
}

/* Title row with booking status badge */
.titleRow {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
}

.title {
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
}

/* ======================
   Booking Status Badge
   ====================== */

.bookingStatusBadge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.bookingStatusBadge[data-status="CONFIRMED"] {
  background: rgba(var(--accent-rgb), 0.16);
  color: var(--accent);
}

.bookingStatusBadge[data-status="PENDING_PAYMENT"],
.bookingStatusBadge[data-status="PENDING_APPROVAL"] {
  background: var(--status-pill-bg);
  color: var(--status-pill-text);
}

.bookingStatusBadge[data-status="WAITLISTED"] {
  background: var(--status-pill-bg);
  color: var(--status-pill-text);
}

/* ======================
   Instance Dates
   ====================== */

.instanceDatesSection {
  margin-bottom: var(--space-4);
}

.instanceDatesLabel {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 var(--space-2);
}

.instanceDatesList {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding-left: var(--space-2);
}

.instanceDateItem {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: 13px;
  color: var(--text-secondary);
}

.instanceDateItem svg {
  flex-shrink: 0;
  color: var(--accent);
}

/* ======================
   Meta Rows
   ====================== */

.meta {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
}

.metaRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-secondary);
  font-size: 14px;
}

.metaRow svg {
  flex-shrink: 0;
  color: var(--accent);
}

/* ======================
   Location Row
   ====================== */

.locationRow {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  color: var(--text-secondary);
  font-size: 14px;
  margin-bottom: var(--space-4);
}

.locationRow svg {
  flex-shrink: 0;
  color: var(--accent);
  margin-top: 2px;
}

.locationText {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.locationSecondary {
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ======================
   Spots Display
   ====================== */

.spotsRow {
  margin-bottom: var(--space-3);
}

.spotsText {
  font-size: 14px;
  font-weight: 600;
}

.spotsText[data-variant="yellow"] {
  color: var(--spots-low-color);
}

.spotsText[data-variant="red"] {
  color: var(--danger);
}

.spotsText[data-variant="muted"] {
  color: var(--text-tertiary);
  font-weight: 400;
}

/* ======================
   Practitioner Row
   ====================== */

.practitionerRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  background: var(--bg-card);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-4);
}

.practitionerInfo {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.practitionerName {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.practitionerSpecialty {
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ======================
   Description
   ====================== */

.description {
  --rich-text-size: 14px;
  --rich-text-leading: 1.6;
  margin: 0 0 var(--space-5);
}

/* ======================
   CTA Button
   ====================== */

.ctaBtn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-6);
  min-height: 56px;
  border: none;
  border-radius: var(--radius-lg);
  background: var(--accent);
  color: var(--text-on-accent);
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  cursor: not-allowed;
  opacity: 0.5;
  box-shadow: var(--shadow-md);
  transition: all 0.2s var(--ease-out);
}

.ctaBtnEnabled {
  cursor: pointer;
  opacity: 1;
}

.ctaBtnEnabled:hover {
  background: var(--accent-deep);
  transform: translateY(-1px);
  box-shadow: var(--shadow-lg);
}

.ctaBtnEnabled:active {
  transform: scale(0.98);
  box-shadow: var(--shadow-sm);
}

.ctaBtnEnabled:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.ctaBtnEnabled:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

.soldOutIndicator {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4) var(--space-6);
  min-height: 56px;
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  color: var(--text-tertiary);
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 500;
}

.ctaBtnViewBooking {
  background: transparent;
  border: 2px solid var(--accent);
  color: var(--accent);
  cursor: pointer;
  opacity: 1;
}

.ctaBtnViewBooking:hover {
  background: var(--bg-card);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.ctaBtnViewBooking:active {
  transform: scale(0.98);
  box-shadow: var(--shadow-sm);
}

.ctaBtnViewBooking:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* ======================
   Error State
   ====================== */

.errorContainer {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-8);
  text-align: center;
}

.errorTitle {
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 var(--space-2);
}

.errorMessage {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 var(--space-5);
  max-width: 280px;
}

.retryBtn {
  padding: var(--space-3) var(--space-6);
  background: var(--accent);
  color: var(--text-on-accent);
  border: none;
  border-radius: var(--radius-md);
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s var(--ease-out);
}

.retryBtn:hover {
  background: var(--accent-deep);
  transform: translateY(-1px);
}

.retryBtn:active {
  transform: scale(0.98);
}

.retryBtn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* ======================
   Skeleton Loaders
   ====================== */

.skeletonBackBtn {
  width: 100px;
  height: 32px;
  background: var(--bg-card);
  border-radius: var(--radius-sm);
  animation: pulse 1.5s ease-in-out infinite;
}

.skeletonImage {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: var(--bg-card);
  animation: pulse 1.5s ease-in-out infinite;
}

.skeletonTitle {
  height: 28px;
  width: 70%;
  background: var(--bg-card);
  border-radius: var(--radius-sm);
  margin-bottom: var(--space-4);
  animation: pulse 1.5s ease-in-out infinite;
}

.skeletonMeta {
  height: 16px;
  width: 50%;
  background: var(--bg-card);
  border-radius: var(--radius-sm);
  margin-bottom: var(--space-2);
  animation: pulse 1.5s ease-in-out infinite;
}

.skeletonDescription {
  height: 60px;
  width: 100%;
  background: var(--bg-card);
  border-radius: var(--radius-sm);
  margin-bottom: var(--space-5);
  animation: pulse 1.5s ease-in-out infinite;
}

.skeletonBtn {
  height: 56px;
  width: 100%;
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  animation: pulse 1.5s ease-in-out infinite;
}

/* ======================
   Animations
   ====================== */

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
